import {
  useApplication,
  ApplicationFormData,
  useCreateApplication,
  useUpdateApplication,
} from "@/api/application/application";
import { useLoggedInUser } from "@/api/user/user";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Center,
  Flex,
  Link,
  ListItem,
  Spinner,
  Text,
  UnorderedList,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { ProfileForm } from "../steps/ProfileForm";
import { defaultRegistryFormData, RegistryFormData } from "..";
import { generalCompletionErrors } from "../validation";
import ErrorSummary from "../ErrorSummary";
import { useTranslation } from "react-i18next";
import { LocalizedLink } from "@/components/i18n/LocalizedLink";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";

export const GeneralPage = () => {
  const applicationQuery = useApplication();
  const applicationData = applicationQuery.data;
  const { data: userData } = useLoggedInUser();
  const { mutateAsync: updateApplication } = useUpdateApplication();
  const { mutateAsync: createApplication } = useCreateApplication();

  const localizedNavigate = useLocalizedNavigate();
  const toast = useToast();
  const { t } = useTranslation(["registry", "common"]);

  const formHook = useForm<RegistryFormData>({
    defaultValues: defaultRegistryFormData,
    mode: "all",
    criteriaMode: "all",
  });

  const formControl = formHook.control;
  const [hydratedFromApplicationId, setHydratedFromApplicationId] = useState<string | null>(null);

  useEffect(() => {
    if (applicationQuery.isLoading) return;
    if (applicationData === null) {
      setHydratedFromApplicationId(null);
      return;
    }
    if (!applicationData) return;
    const latest = formHook.getValues();
    formHook.reset({
      ...latest,
      application: {
        ...latest.application,
        ...applicationData,
      },
    });
    setHydratedFromApplicationId(applicationData.id);
  }, [applicationData, applicationQuery.isLoading, formHook]);

  const application = useWatch({
    control: formControl,
    name: "application",
  });

  const isSubmitted = !!application?.is_submitted;

  const generalErrors = useMemo(() => {
    return generalCompletionErrors(application);
  }, [application]);

  const generalIsComplete = useMemo(() => {
    return !generalErrors || generalErrors.length === 0;
  }, [generalErrors]);

  const generalLocked = !!applicationData?.consent_locked;

  const saveAndContinue = useCallback(async () => {
    if (!userData?.user?.id) return;

    const valid = await formHook.trigger();
    if (!valid) return;

    const payload: ApplicationFormData = {
      ...formHook.getValues().application,
      user_id: userData.user.id,
      is_submitted: false,
    };

    try {
      if (payload.id) {
        await updateApplication({ id: payload.id, ...payload });
      } else {
        await createApplication(payload);
      }

      toast({
        title: payload.id ? t("common:toast.saved") : t("common:toast.applicationStarted"),
        description: t("registry:general.generalInfoSaved"),
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      localizedNavigate("/registry/consent");
      window.scrollTo(0, 0);
    } catch (error) {
      const message = error instanceof Error ? error.message : t("common:errors.unknownError");
      toast({
        title: t("common:toast.error"),
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [
    createApplication,
    formHook,
    localizedNavigate,
    toast,
    updateApplication,
    userData?.user?.id,
    t,
  ]);

  const handleContinue = useCallback(async () => {
    // Once general info is saved complete (or application is submitted), treat this step as read-only.
    if (generalLocked || isSubmitted) {
      localizedNavigate("/registry/consent");
      window.scrollTo(0, 0);
      return;
    }

    await saveAndContinue();
  }, [generalLocked, isSubmitted, localizedNavigate, saveAndContinue]);

  const isHydrated =
    applicationQuery.isLoading === false &&
    (applicationData === null ||
      (applicationData?.id && hydratedFromApplicationId === applicationData.id));

  if (!isHydrated) {
    return (
      <Center paddingY={16}>
        <VStack spacing={4}>
          <Spinner size="lg" />
          <Text fontSize="lg" color="gray.700">
            {t("common:status.loadingApplication")}
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack spacing={6} alignItems="stretch" paddingBlockEnd={24}>
      <Flex fontSize="md" alignItems="baseline" gap={1} color="gray.600" fontWeight="semibold">
        <Link as={LocalizedLink} color="blue.500" to="/">
          &larr; {t("common:buttons.backToStepList")}
        </Link>
      </Flex>

      <ProfileForm
        formControl={formControl}
        setValue={formHook.setValue}
        isLocked={generalLocked}
        isSubmitted={isSubmitted}
        applicantType={application?.applicant_type ?? "self"}
      />

      <Button
        data-testid="save-and-continue-button"
        colorScheme="blue"
        paddingInline={20}
        paddingY={8}
        fontSize="xl"
        width="full"
        maxWidth="container.md"
        marginInline="auto"
        onClick={handleContinue}
        isDisabled={!isHydrated || !generalIsComplete}
      >
        {generalLocked || isSubmitted
          ? t("common:buttons.continue")
          : t("common:buttons.saveAndContinue")}
      </Button>

      <ErrorSummary errors={generalErrors} />
    </VStack>
  );
};
